import { DateTime } from 'luxon';
import { assoc, dissoc, indexBy, mergeLeft, prepend, without } from 'ramda';
import { AnyAction, combineReducers } from 'redux';

import {
  isClearRecentlyModified,
  isCreateCheckpointTypeSuccess,
  isDeleteCheckpointTypeSuccess,
  isGetCheckpointTypeCountSuccess,
  isGetCheckpointTypesSuccess,
  isGetCheckpointTypeSuccess,
  isUpdateCheckpointTypeSuccess
} from './actions';
import {
  CheckpointType,
  CheckpointTypeResponseDto,
  CheckpointTypes
} from './state';

export const INITIAL_STATE: CheckpointTypes = {
  allCodes: [],
  byCode: {},
  count: null,
  recentlyModified: []
};

export const checkpointTypes = combineReducers({
  allCodes,
  byCode,
  count,
  recentlyModified
});

const indexByCode = indexBy<CheckpointType>(checkpointType => checkpointType.code);

export const parseCheckpointType = (response: CheckpointTypeResponseDto): CheckpointType => ({
  ...response,
  created: DateTime.fromISO(response.created),
  updated: DateTime.fromISO(response.updated)
});

function byCode(state = INITIAL_STATE.byCode, action: AnyAction): typeof INITIAL_STATE.byCode {
  if (isGetCheckpointTypesSuccess(action)) {
    const parsed = action.payload.data.map(parseCheckpointType);
    return mergeLeft(indexByCode(parsed), state);
  } else if (isDeleteCheckpointTypeSuccess(action)) {
    return dissoc(action.meta.previousAction.code, state);
  } else if (
    isCreateCheckpointTypeSuccess(action)
    || isUpdateCheckpointTypeSuccess(action)
    || isGetCheckpointTypeSuccess(action)
  ) {
    return assoc(action.payload.data.code, parseCheckpointType(action.payload.data), state);
  }

  return state;
}

function allCodes(state = INITIAL_STATE.allCodes, action: AnyAction): typeof INITIAL_STATE.allCodes {
  if (isGetCheckpointTypesSuccess(action) && !action.meta.previousAction.transient) {
    return action.payload.data.map(d => d.code);
  } else if (isDeleteCheckpointTypeSuccess(action)) {
    return without([action.meta.previousAction.code], state);
  }

  return state;
}

function count(state = INITIAL_STATE.count, action: AnyAction): typeof INITIAL_STATE.count {
  if (isGetCheckpointTypeCountSuccess(action)) {
    return action.payload.data.count;
  } else if (isDeleteCheckpointTypeSuccess(action) && state !== null) {
    return state - 1;
  } else if (isCreateCheckpointTypeSuccess(action) && state !== null) {
    return state + 1;
  }

  return state;
}

function recentlyModified(
  state = INITIAL_STATE.recentlyModified,
  action: AnyAction
): typeof INITIAL_STATE.recentlyModified {
  if (isClearRecentlyModified(action)) {
    return [];
  } else if (isDeleteCheckpointTypeSuccess(action)) {
    return without([action.meta.previousAction.code], state);
  } else if (isCreateCheckpointTypeSuccess(action) || isUpdateCheckpointTypeSuccess(action)) {
    return prepend(action.payload.data.code, state);
  }

  return state;
}
